import { useEffect, useRef, useState, useCallback } from "react";
import styles from "./CrossteamSite.module.css";

/*
 * 크로스팀 — 시공관리 PMIS 플랫폼 랜딩페이지
 * ------------------------------------------------------------------
 * 원본 HTML을 단일 React 컴포넌트로 변환한 파일입니다.
 *  - 스타일: CSS Modules (CrossteamSite.module.css)
 *  - 다국어(KO/EN): I18N 딕셔너리 + useState/localStorage
 *  - 기능 캐러셀: useRef 스크롤 제어 + 도트 인디케이터
 *  - 스크롤 등장 애니메이션: IntersectionObserver
 *
 * 이미지 파일(Hero_web.png 등)은 프로젝트의 `public/` 폴더에 넣고
 * 아래 ASSET 경로를 환경에 맞게 조정하세요. (기본값: public 루트)
 */

// ─────────────────────────────────────────────────────────────
// i18n 딕셔너리 (원본 그대로. 필요 시 locales/ko.json, en.json 으로 분리 가능)
// ─────────────────────────────────────────────────────────────
const I18N = {
  ko: {
    "nav.useCases": "활용방법", "nav.features": "주요기능", "nav.ai": "AI기능", "nav.customers": "도입사례", "nav.login": "로그인",
    "cta.trial": "1개월 무료 체험", "cta.trialLong": "1개월 무료로 시작하기", "cta.contact": "문의하기", "cta.mail": "메일로 문의하기",
    "hero.kicker": "시공관리 PMIS 플랫폼", "hero.title": "현장 업무를<br>스마트하게<br>하는 방법",
    "hero.lead": "작업일보부터 검측, 품질문서까지. 웹과 모바일 앱에서 현장의 문서 업무를 자동으로 잇습니다.",
    "hero.note": "웹과 모바일 앱 · 협력업체 계정 포함",
    "trust.label": "국내 주요 건설사 현장에서 사용 중",
    "use.eyebrow": "HOW TO USE", "use.title": "크로스팀의 세 가지 활용 방법", "use.lead": "설계부터 준공까지, 현장의 모든 기록을 하나의 흐름으로 묶습니다.",
    "use.c1.t": "단계별 이력관리", "use.c1.d": "설계부터 준공까지의 기록이 한 흐름으로 남습니다. 어느 단계에서 무엇을 진행했는지 목록만 봐도 알 수 있습니다.",
    "use.c2.t": "간편한 BIM 활용", "use.c2.d": "현장 누구나 3D 모델을 열어 보고 의견을 남깁니다. 레빗, 나비스웍스, 스케치업 파일을 그대로 올릴 수 있습니다.",
    "use.c3.t": "효율적 문서관리", "use.c3.d": "감리·시공사·협력업체가 함께 작성하고 전자결재까지 한곳에서 끝냅니다.<br><br>준공시 자료이관이 쉬워집니다.",
    "tag.addon": "부가서비스", "tag.addonShort": "부가", "tag.new": "신규", "tag.soon": "출시 예정",
    "why.title": "한 번 입력하면, 문서는 따라옵니다", "why.lead": "다시 옮겨 적는 일이 사라지면, 현장의 하루가 달라집니다.",
    "why.c1.t": "취합하지 않아도 모입니다", "why.c1.d": "협력업체가 각자 올린 작업 내용과 출역 인원이 자동으로 한곳에 정리됩니다.",
    "why.c2.t": "일하는 과정이 곧 기록이 됩니다", "why.c2.d": "따로 정리하지 않아도 그날의 업무가 문서로 남고, 전자결재까지 이어집니다.",
    "why.c3.t": "업무인수인계가 간단해집니다", "why.c3.d": "담당자가 바뀌어도 문서와 이력이 서버에 남아 있어, 이어받는 사람이 바로 시작합니다.",
    "why.band": "서류에 쓰던 시간을 현장에 씁니다", "why.link": "대표 기고문 · 사람이 떠난 현장, 무엇이 남는가 →",
    "feat.eyebrow": "FEATURES", "feat.title": "주요기능", "feat.lead": "현장에서 매일 쓰는 기능부터 새로 더해진 기능까지.",
    "f1.t": "통합 대시보드", "f1.d": "공정률과 출역 현황, 세부 진척도를 하나의 화면에서 실시간으로 확인할 수 있습니다.",
    "f2.t": "작업일보 · 출역일보", "f2.d": "협력업체가 제출한 작업일보와 출역 현황이 자동으로 취합되어 별도 정리가 필요 없습니다.",
    "f3.t": "자재 승인 · 수불부", "f3.d": "검수요청서를 작성하면 자재 수불부까지 자동으로 반영되어 이중 입력이 사라집니다.",
    "f4.t": "레미콘 품질문서", "f4.d": "현장 점검표 작성 결과가 품질관리 대장까지 그대로 연동되어 이력이 끊기지 않습니다.",
    "f5.t": "균열관리 · 보수대장", "f5.d": "표준 지침 양식으로 균열 발생 위치와 진행 상태를 기록하고 보수 이력까지 추적합니다.",
    "f6.t": "공정관리", "f6.d": "세대별 골구도와 옵션 진행 현황을 유닛 단위로 세밀하게 관리할 수 있습니다.",
    "f7.t": "일정관리", "f7.d": "Gantt-Bar 기반 일정표로 공정 간 선후관계와 지연 여부를 한눈에 파악합니다.",
    "f8.t": "3D 뷰어", "f8.d": "BIM 모델을 웹에서 바로 열어 살펴보고, 위치별로 주석과 의견을 남길 수 있습니다.",
    "f9.t": "드라이브 · 사진첩", "f9.d": "문서 버전관리와 360도 현장 사진을 한곳에 모아 언제든 다시 찾아볼 수 있습니다.",
    "f10.t": "전자결재 · 권한관리", "f10.d": "소속과 직책에 따라 권한과 결재선을 설정해, 문서마다 알맞은 절차로 승인됩니다.",
    "insp.eyebrow": "INSPECTION", "insp.title": "버튼 클릭 몇 번으로<br>검측문서 완료",
    "insp.lead": "언제 어디서나 버튼 몇 번만 클릭하면 문서를 한번에 생성합니다. 감리단과 함께 촬영한 사진을 첨부하고, 전자결재로 빠르게 완료할 수 있습니다. 승인을 받으러 다닐 필요없이 푸쉬알림으로 간편하게 커뮤니케이션 할 수 있습니다.",
    "insp.p1": "위치별, 공종별 깔끔한 정리", "insp.p2": "현장별 체크리스트 별도 관리", "insp.p3": "간편한 사진첨부 및 보드판 입력까지 한번에",
    "ai.eyebrow": "AI FEATURE", "ai.title": "쌓인 기록 위에서 AI가 일합니다", "ai.lead": "현장에서 작성된 데이터를 AI가 읽고 정리합니다.",
    "ai.c1.t": "보드판 자동 문서화", "ai.c1.d": "현장 보드판을 찍으면 AI가 내용을 읽어 문서로 만듭니다. 옮겨 적는 과정이 사라집니다.",
    "ai.c2.t": "AI 품질 대시보드", "ai.c2.d": "클릭만으로 XR관리도를 제공합니다. 레미콘 품질자료를 AI가 분석하여 시험값 추이와 이상 징후를 알려줍니다.",
    "role.eyebrow": "FOR EVERY ROLE", "role.title": "프로젝트에 참여하는 모두에게<br>필요한 기능을 제공합니다",
    "role.c1.t": "발주처", "role.c1.a": "현장 상황과 일정을 한눈에", "role.c1.b": "사진과 360도, 3D까지 확인", "role.c1.c": "CM·시공사와의 창구를 하나로",
    "role.c2.t": "CM · 감리", "role.c2.a": "보고에 드는 작업 시간이 줄어듦", "role.c2.b": "축적된 정보는 보고서에 활용", "role.c2.c": "이력이 한곳에 남아 추적이 쉬움",
    "role.c3.t": "시공사", "role.c3.a": "협력업체 정보가 자동으로 취합", "role.c3.b": "공유로 중복 작업이 줄어듦", "role.c3.c": "권한 설정과 간단한 인수인계",
    "cust.title": "일반 건축물부터 공동주택까지", "cust.lead": "국내 주요 건설 현장에서 검증되었습니다.",
    "cta.title": "현장에 맞는 도입 방법을<br>안내해 드립니다", "cta.lead": "문의를 남겨주시면 서비스 소개서와 도입 가이드를 함께 보내드립니다.",
    "foot.h1": "정보", "foot.h3": "안내", "foot.co": "크로스빔㈜", "foot.addr": "세종특별자치시 조치원읍 군청로 93, 3층",
    "foot.careers": "채용", "foot.privacy": "개인정보처리방침", "foot.terms": "이용약관", "foot.tag": "시공관리 PMIS 플랫폼", "foot.blog": "블로그", "foot.youtube": "유튜브", "foot.facebook": "페이스북",
  },
  en: {
    "nav.useCases": "How to use", "nav.features": "Features", "nav.ai": "AI features", "nav.customers": "Customers", "nav.login": "Log in",
    "cta.trial": "Free for 1 month", "cta.trialLong": "Start your free month", "cta.contact": "Contact us", "cta.mail": "Email us",
    "hero.kicker": "Construction Management PMIS", "hero.title": "A smarter way<br>to run your<br>jobsite",
    "hero.lead": "From daily reports to inspections and quality records — Crossteam connects your site paperwork automatically, on web and mobile.",
    "hero.note": "Web and mobile app · Subcontractor accounts included",
    "trust.label": "In use on sites of leading Korean construction firms",
    "use.eyebrow": "HOW TO USE", "use.title": "Three ways teams use Crossteam", "use.lead": "Every record your site produces, from design to handover, kept in one continuous flow.",
    "use.c1.t": "Records at every stage", "use.c1.d": "Everything from design to completion stays in one flow — a quick look at the list shows what happened at each stage.",
    "use.c2.t": "BIM made simple", "use.c2.d": "Anyone on site can open the 3D model and leave comments. Upload Revit, Navisworks and SketchUp files as they are.",
    "use.c3.t": "Documents that work together", "use.c3.d": "Supervisors, contractors and partners write together — approvals finish in the same place.<br><br>Handover at completion becomes easy too.",
    "tag.addon": "Add-on", "tag.addonShort": "Add-on", "tag.new": "New", "tag.soon": "Coming soon",
    "why.title": "Enter it once. The documents follow.", "why.lead": "When re-typing disappears, the day on site changes.",
    "why.c1.t": "No collecting required", "why.c1.d": "What each subcontractor submits gathers itself into one place, automatically.",
    "why.c2.t": "The work becomes the record", "why.c2.d": "Without separate write-ups, the day's work turns into a document and moves on to approval.",
    "why.c3.t": "Work handover made simple", "why.c3.d": "Documents and history stay on the server, so whoever takes over starts where you left off.",
    "why.band": "Spend on site the hours you spent on paperwork", "why.link": "Read our founder's column · What remains when people leave →",
    "feat.eyebrow": "FEATURES", "feat.title": "Features", "feat.lead": "From what your site uses every day to what we have just added.",
    "f1.t": "Unified dashboard", "f1.d": "See progress rate, attendance and detailed completion status together in real time, in one view.",
    "f2.t": "Daily & attendance reports", "f2.d": "Daily reports and attendance submitted by subcontractors gather automatically, with no separate compiling.",
    "f3.t": "Material approval & ledger", "f3.d": "Filing an inspection request updates the material ledger automatically, removing double entry.",
    "f4.t": "Concrete quality records", "f4.d": "Site checklist results flow straight into the quality register, keeping the history unbroken.",
    "f5.t": "Crack & repair register", "f5.d": "Log crack locations and progress on the standard format, and trace the repair history over time.",
    "f6.t": "Progress by unit", "f6.d": "Manage structure grids and option progress in fine detail, unit by unit.",
    "f7.t": "Scheduling", "f7.d": "Gantt-bar schedules make dependencies and delays between trades visible at a glance.",
    "f8.t": "3D viewer", "f8.d": "Open BIM models right in the browser and leave location-based annotations and comments.",
    "f9.t": "Drive & photo album", "f9.d": "Keep document versions and 360° site photos together, always easy to find again.",
    "f10.t": "Approvals & permissions", "f10.d": "Set permissions and approval lines by company and role, so every document follows the right process.",
    "insp.eyebrow": "INSPECTION", "insp.title": "A few clicks and the<br>inspection document is done",
    "insp.lead": "A few clicks anywhere generate the document at once. Attach photos taken with the supervisor and finish quickly with e-approval. No need to chase approvals in person — push notifications keep everyone in sync.",
    "insp.p1": "Clean organization by location and trade", "insp.p2": "Separate checklists managed per site", "insp.p3": "Easy photo attachments and board entries, all at once",
    "ai.eyebrow": "AI FEATURE", "ai.title": "AI works on the records you already keep", "ai.lead": "AI reads and organizes the data your site produces.",
    "ai.c1.t": "Board-to-document", "ai.c1.d": "Photograph the site board and AI reads it into a document. The copying step disappears.",
    "ai.c2.t": "AI quality dashboard", "ai.c2.d": "One click gives you XR control charts — AI analyses concrete quality data to show trends and flag anomalies.",
    "role.eyebrow": "FOR EVERY ROLE", "role.title": "Built for everyone<br>on the project",
    "role.c1.t": "Owners", "role.c1.a": "Site status and schedule at a glance", "role.c1.b": "Photos, 360° views and 3D models", "role.c1.c": "One channel to CM and contractors",
    "role.c2.t": "CM & supervision", "role.c2.a": "Less time spent preparing reports", "role.c2.b": "Accumulated data feeds straight into reports", "role.c2.c": "History in one place, easy to trace",
    "role.c3.t": "Contractors", "role.c3.a": "Subcontractor data gathers itself", "role.c3.b": "Shared access cuts duplicate work", "role.c3.c": "Simple permissions, easy handover",
    "cust.title": "From commercial buildings to housing complexes", "cust.lead": "Proven on major construction sites across Korea.",
    "cta.title": "We will walk you through<br>the right setup for your site", "cta.lead": "Send us a note and we will reply with an overview and an onboarding guide.",
    "foot.h1": "Info", "foot.h3": "Support", "foot.co": "CROSSBIM Inc.", "foot.addr": "93 Guncheong-ro, Jochiwon-eup, Sejong, Korea",
    "foot.careers": "Careers", "foot.privacy": "Privacy policy", "foot.terms": "Terms of service", "foot.tag": "Construction Management PMIS", "foot.blog": "Blog", "foot.youtube": "YouTube", "foot.facebook": "Facebook",
  },
};

// 이미지 경로 (public 폴더 기준). 번들러/배포 환경에 맞게 수정하세요.
const ASSET = {
  logo: "/crossteam_logo.svg",
  heroWeb: "/Hero_web.png",
  heroApp: "/Hero_app.png",
  inspWeb: "/inspection_web.png",
  inspApp: "/inspection_app.png",
  aiQuality: "/1_AI품질_dashboard.png",
  aiBoard: "/2_AI보드판_다이어그램_라이트.png",
  logos: [
    { src: "/hoban.png", alt: "호반건설" },
    { src: "/CJ.png", alt: "CJ대한통운" },
    { src: "/bando.png", alt: "반도건설" },
    { src: "/Kyeryong.png", alt: "계룡건설" },
    { src: "/heerim.png", alt: "희림CM" },
    { src: "/spaceDBM.png", alt: "spaceDBM" },
  ],
};

const LINKS = {
  login: "https://adm.crossteam.co.kr",
  signup: "https://adm.crossteam.co.kr/join",
  contactEmail: "mailto:support@crossteam.co.kr?subject=%ED%81%AC%EB%A1%9C%EC%8A%A4%ED%8C%80%20%EB%8F%84%EC%9E%85%20%EB%AC%B8%EC%9D%98",
  founderColumn: "http://www.conslove.co.kr/news/articleView.html?idxno=88930",
  privacy: "https://adm.crossteam.co.kr/policy/privacy",
  terms: "https://adm.crossteam.co.kr/policy/service",
  blog: "https://blog.naver.com/crossbim001",
  youtube: "https://www.youtube.com/channel/UCYT38wvy1wcPjvavidrxxsw",
  facebook: "https://www.facebook.com/crossteam911",
};

// 데이터 배열
const USE_CASES = [
  { icon: "i-layers", k: "use.c1" },
  { icon: "i-cube", k: "use.c2", tag: { cls: "opt", key: "tag.addon" } },
  { icon: "i-doc", k: "use.c3" },
];

const WHYS = [
  { n: "01", k: "why.c1" },
  { n: "02", k: "why.c2" },
  { n: "03", k: "why.c3" },
];

const FEATURES = [
  { icon: "i-chart", k: "f1" },
  { icon: "i-doc", k: "f2" },
  { icon: "i-checkbox", k: "f3" },
  { icon: "i-flask", k: "f4" },
  { icon: "i-crack", k: "f5" },
  { icon: "i-grid", k: "f6" },
  { icon: "i-calendar", k: "f7" },
  { icon: "i-cube", k: "f8", tag: { cls: "opt", key: "tag.addonShort" } },
  { icon: "i-folder", k: "f9" },
  { icon: "i-check", k: "f10" },
];

const ROLES = [
  { icon: "i-building", k: "role.c1" },
  { icon: "i-clip", k: "role.c2" },
  { icon: "i-helmet2", k: "role.c3" },
];

// class 이름 여러 개를 CSS Module 해시로 매핑해 합치는 헬퍼
const cx = (...names) =>
  names.filter(Boolean).map((n) => styles[n] || n).join(" ");

// ─────────────────────────────────────────────────────────────
export default function CrossteamSite() {
  const [lang, setLang] = useState("ko");
  const trackRef = useRef(null);
  const rootRef = useRef(null);
  const [activeDot, setActiveDot] = useState(0);

  // 번역 헬퍼
  const t = useCallback((key) => I18N[lang][key] ?? key, [lang]);
  // HTML(<br> 등)이 포함된 텍스트를 렌더링하는 헬퍼 컴포넌트
  const T = useCallback(
    ({ as: Tag = "span", k, ...rest }) => (
      <Tag {...rest} dangerouslySetInnerHTML={{ __html: t(k) }} />
    ),
    [t]
  );

  // 최초 마운트 시 저장된 언어 복원 + <html lang> 갱신
  useEffect(() => {
    try {
      const saved = localStorage.getItem("ct-lang");
      if (saved === "ko" || saved === "en") setLang(saved);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    try { localStorage.setItem("ct-lang", lang); } catch (e) { /* ignore */ }
  }, [lang]);

  // 스크롤 등장 애니메이션
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const items = root.querySelectorAll(`.${styles.rv}`);
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add(styles.in);
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0.08 }
    );
    items.forEach((el, i) => {
      el.style.transitionDelay = `${(i % 3) * 70}ms`;
      io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  // 기능 캐러셀: 스크롤 위치에 따라 활성 도트 갱신
  const STEP = 292;
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const i = Math.min(
        FEATURES.length - 1,
        Math.round(track.scrollLeft / STEP)
      );
      setActiveDot(i);
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    return () => track.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTrack = (dir) => {
    trackRef.current?.scrollBy({ left: dir * STEP * 2, behavior: "smooth" });
  };

  return (
    <div ref={rootRef}>
      <IconSprite />

      {/* ══ header ══ */}
      <header>
        <div className={cx("wrap", "hd")}>
          <a className={cx("brand")} href="#top">
            <img src={ASSET.logo} alt="크로스팀" style={{ height: 24, width: "auto", display: "block" }} />
          </a>
          <nav className={cx("gnb")}>
            <a href="#use-cases"><T k="nav.useCases" /></a>
            <a href="#features"><T k="nav.features" /></a>
            <a href="#ai-features"><T k="nav.ai" /></a>
            <a href="#customers"><T k="nav.customers" /></a>
          </nav>
          <div className={cx("hd-r")}>
            <div className={cx("lang")}>
              <button className={lang === "ko" ? cx("on") : undefined} onClick={() => setLang("ko")}>KO</button>
              <button className={lang === "en" ? cx("on") : undefined} onClick={() => setLang("en")}>EN</button>
            </div>
            <a className={cx("btn", "sm")} href={LINKS.login} target="_blank" rel="noreferrer"><T k="nav.login" /></a>
            <a className={cx("btn", "p", "sm")} href={LINKS.signup} target="_blank" rel="noreferrer"><T k="cta.trial" /></a>
            <button className={cx("burger")} aria-label="menu">
              <svg width="20" height="20" viewBox="0 0 24 24" stroke="var(--ink)"><use href="#i-menu" /></svg>
            </button>
          </div>
        </div>
      </header>

      {/* ══ hero ══ */}
      <div className={cx("hero")} id="top">
        <div className={cx("wrap")}>
          <div>
            <div className={cx("eyebrow")}>
              <i className={cx("rule")} />
              <T k="hero.kicker" style={{ fontSize: 14 }} />
            </div>
            <T as="h1" k="hero.title" />
            <T as="p" className={cx("lead")} k="hero.lead" />
            <div className={cx("cta")}>
              <a className={cx("btn", "p")} href={LINKS.signup} target="_blank" rel="noreferrer"><T k="cta.trialLong" /></a>
              <a className={cx("btn")} href={LINKS.contactEmail}><T k="cta.contact" /></a>
            </div>
          </div>

          {/* 제품 화면 목업 */}
          <div className={cx("shot")} style={{ position: "relative", width: "100%", maxWidth: 560, marginLeft: "auto", paddingBottom: 56, paddingRight: 34 }}>
            <img src={ASSET.heroWeb} alt="크로스팀 웹 대시보드 화면"
              style={{ width: "100%", display: "block", borderRadius: 12, boxShadow: "0 32px 60px -20px rgba(14,74,88,.30)", border: "4px solid #0E4A58" }} />
            <img src={ASSET.heroApp} alt="크로스팀 모바일 앱 화면"
              style={{ position: "absolute", right: "var(--mock-app-right, -34px)", bottom: -56, width: "32%", display: "block", borderRadius: 22, boxShadow: "0 24px 48px -16px rgba(14,74,88,.34)", border: "4px solid #0E4A58" }} />
          </div>
        </div>
      </div>

      {/* ══ trust ══ */}
      <div className={cx("trust")} />

      {/* ══ 세 가지 활용 방법 ══ */}
      <section id="use-cases">
        <div className={cx("wrap")}>
          <div className={cx("head", "c", "rv")}>
            <div className={cx("eyebrow", "c")}>
              <i className={cx("rule")} />
              <T k="use.eyebrow" style={{ fontSize: 14 }} />
            </div>
            <T as="h2" k="use.title" />
            <T as="p" k="use.lead" />
          </div>
          <div className={cx("g3")}>
            {USE_CASES.map((u) => (
              <div className={cx("card", "rv")} key={u.k}>
                <div className={cx("ico")}>
                  <svg width="24" height="24" viewBox="0 0 24 24" stroke="#17915A"><use href={`#${u.icon}`} /></svg>
                </div>
                <h3>
                  <T k={`${u.k}.t`} />
                  {u.tag && <T className={cx("tag", u.tag.cls)} k={u.tag.key} />}
                </h3>
                <T as="p" k={`${u.k}.d`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 왜 필요한가 ══ */}
      <section className={cx("s-tint")}>
        <div className={cx("wrap")}>
          <div className={cx("head", "c", "rv")}>
            <T as="h2" k="why.title" />
            <T as="p" k="why.lead" />
          </div>
          <div className={cx("why", "rv")}>
            {WHYS.map((w) => (
              <div key={w.k}>
                <p className={cx("n", "tnum")}>{w.n}</p>
                <T as="h3" k={`${w.k}.t`} />
                <T as="p" k={`${w.k}.d`} />
              </div>
            ))}
          </div>
          <div className={cx("band", "rv")}>
            <T as="strong" k="why.band" />
            <a href={LINKS.founderColumn} target="_blank" rel="noreferrer"><T k="why.link" /></a>
          </div>
        </div>
      </section>

      {/* ══ 스마트 검측 ══ */}
      <section className={cx("s-tint")}>
        <div className={cx("wrap", "split")}>
          <div className={cx("rv")} style={{ position: "relative", width: "100%", maxWidth: 560, paddingBottom: 56, paddingRight: 34 }}>
            <div style={{ width: "100%", aspectRatio: "1170 / 761", overflow: "hidden", borderRadius: 12, border: "4px solid #0E4A58", boxShadow: "var(--shadow-m)" }}>
              <img src={ASSET.inspWeb} alt="크로스팀 검측 문서관리 화면" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block", overflowClipMargin: "unset" }} />
            </div>
            <div style={{ position: "absolute", right: "var(--mock-app-right, -34px)", bottom: -56, width: "32%", aspectRatio: "1440 / 3072", overflow: "hidden", borderRadius: 22, boxShadow: "var(--shadow-l)", border: "4px solid #0E4A58" }}>
              <img src={ASSET.inspApp} alt="크로스팀 검측 체크리스트 모바일 화면" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block", overflowClipMargin: "unset" }} />
            </div>
          </div>
          <div className={cx("rv")}>
            <div className={cx("eyebrow")}>
              <i className={cx("rule")} />
              <T k="insp.eyebrow" style={{ fontSize: 14 }} />
            </div>
            <T as="h2" k="insp.title" />
            <T as="p" className={cx("lead")} k="insp.lead" />
            <ul className={cx("checks")}>
              {["insp.p1", "insp.p2", "insp.p3"].map((p) => (
                <li key={p}>
                  <svg width="18" height="18" viewBox="0 0 24 24" stroke="#2BB673"><use href="#i-check" /></svg>
                  <T k={p} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ══ 주요기능 스와이프 ══ */}
      <section id="features">
        <div className={cx("wrap")}>
          <div className={cx("swipe-top", "rv")}>
            <div className={cx("head")}>
              <div className={cx("eyebrow")}>
                <i className={cx("rule")} />
                <T k="feat.eyebrow" style={{ fontSize: 14 }} />
              </div>
              <T as="h2" k="feat.title" />
            </div>
            <div className={cx("arrows")}>
              <button data-dir="-1" aria-label="이전" onClick={() => scrollTrack(-1)}>
                <svg width="18" height="18" viewBox="0 0 24 24" stroke="currentColor"><use href="#i-l" /></svg>
              </button>
              <button data-dir="1" aria-label="다음" onClick={() => scrollTrack(1)}>
                <svg width="18" height="18" viewBox="0 0 24 24" stroke="currentColor"><use href="#i-r" /></svg>
              </button>
            </div>
          </div>
        </div>

        <div className={cx("wrap")}>
          <div className={cx("track")} ref={trackRef}>
            {FEATURES.map((f) => (
              <article className={cx("fc")} key={f.k}>
                <div className={cx("icon")}>
                  <svg viewBox="0 0 24 24"><use href={`#${f.icon}`} /></svg>
                </div>
                <h3>
                  <T k={`${f.k}.t`} />
                  {f.tag && <T className={cx("tag", f.tag.cls)} k={f.tag.key} />}
                </h3>
                <T as="p" k={`${f.k}.d`} />
              </article>
            ))}
          </div>
        </div>

        <div className={cx("wrap")}>
          <div className={cx("dots")}>
            {FEATURES.map((_, i) => (
              <i key={i} className={i === activeDot ? cx("on") : undefined} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ AI ══ */}
      <section className={cx("s-dark")} id="ai-features">
        <div className={cx("wrap")}>
          <div className={cx("head", "c", "rv")}>
            <div className={cx("eyebrow", "c")}>
              <i className={cx("rule")} />
              <T k="ai.eyebrow" style={{ color: "#7FD4AC", fontSize: 14 }} />
            </div>
            <T as="h2" k="ai.title" />
            <T as="p" k="ai.lead" />
          </div>
          <div className={cx("g2")}>
            <div className={cx("card", "rv")} style={{ background: "#EEF2F6", borderColor: "rgba(16,24,40,.08)" }}>
              <div style={{ aspectRatio: "5 / 3", borderRadius: 10, marginBottom: 22, overflow: "hidden", background: "#EEF2F6" }}>
                <img src={ASSET.aiQuality} alt="AI 품질 분석 대시보드" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
              </div>
              <h3 style={{ color: "#101828" }}><T k="ai.c2.t" /></h3>
              <T as="p" style={{ color: "#4B5768" }} k="ai.c2.d" />
            </div>
            <div className={cx("card", "rv")} style={{ background: "#EEF2F6", borderColor: "rgba(16,24,40,.08)" }}>
              <div style={{ aspectRatio: "5 / 3", borderRadius: 10, marginBottom: 22, overflow: "hidden", background: "#EEF2F6" }}>
                <img src={ASSET.aiBoard} alt="보드판을 촬영해 문서로 변환" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
              </div>
              <h3 style={{ color: "#101828" }}>
                <T k="ai.c1.t" />
                <T className={cx("tag", "soon")} k="tag.soon" />
              </h3>
              <T as="p" style={{ color: "#4B5768" }} k="ai.c1.d" />
            </div>
          </div>
        </div>
      </section>

      {/* ══ 관계자별 ══ */}
      <section>
        <div className={cx("wrap")}>
          <div className={cx("head", "c", "rv")}>
            <div className={cx("eyebrow", "c")}>
              <i className={cx("rule")} />
              <T k="role.eyebrow" style={{ fontSize: 14 }} />
            </div>
            <T as="h2" k="role.title" />
          </div>
          <div className={cx("g3")}>
            {ROLES.map((r) => (
              <div className={cx("role", "rv")} key={r.k}>
                <div className={cx("rt")}>
                  <span className={cx("ico")} style={{ width: 40, height: 40, borderRadius: 11, margin: 0 }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" stroke="#0E4A58"><use href={`#${r.icon}`} /></svg>
                  </span>
                  <T as="h3" k={`${r.k}.t`} />
                </div>
                <ul>
                  <li><T k={`${r.k}.a`} /></li>
                  <li><T k={`${r.k}.b`} /></li>
                  <li><T k={`${r.k}.c`} /></li>
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 도입기업 ══ */}
      <section className={cx("s-tint")} id="customers">
        <div className={cx("wrap")}>
          <div className={cx("head", "c", "rv")}>
            <T as="h2" k="cust.title" />
            <T as="p" k="cust.lead" />
          </div>
          <div className={cx("logo-wall", "rv")}>
            {ASSET.logos.map((logo) => (
              <div className={cx("logo-cell")} key={logo.alt}>
                <div className={cx("box")}>
                  <img src={logo.src} alt={logo.alt} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ 문의 ══ */}
      <section id="contact">
        <div className={cx("wrap")}>
          <div className={cx("cta-box", "rv")}>
            <T as="h2" k="cta.title" />
            <div className={cx("cta-row")}>
              <a className={cx("btn", "p")} href={LINKS.signup} target="_blank" rel="noreferrer"><T k="cta.trialLong" /></a>
              <a className={cx("btn", "ghost")} href={LINKS.contactEmail}><T k="cta.mail" /></a>
            </div>
            <p className={cx("cta-mail")}>
              <svg width="15" height="15" viewBox="0 0 24 24" stroke="#7FD4AC" style={{ display: "inline-block", verticalAlign: -3, marginRight: 7 }}><use href="#i-mail" /></svg>
              support@crossteam.co.kr &nbsp;·&nbsp; 02-2038-3014
            </p>
          </div>
        </div>
      </section>

      {/* ══ 푸터 ══ */}
      <footer id="company">
        <div className={cx("wrap")}>
          <div className={cx("fg")}>
            <div>
              <div className={cx("fbrand")}>
                <img src={ASSET.logo} alt="크로스팀" style={{ height: 20, width: "auto", filter: "brightness(0) invert(1)" }} />
              </div>
              <T as="span" k="foot.co" />
              <T as="span" k="foot.addr" />
              <span>02-2038-3014 · support@crossteam.co.kr</span>
            </div>
            <div>
              <T as="h4" k="foot.h3" />
              <a href={LINKS.privacy} target="_blank" rel="noreferrer"><T k="foot.privacy" /></a>
              <a href={LINKS.terms} target="_blank" rel="noreferrer"><T k="foot.terms" /></a>
            </div>
            <div>
              <T as="h4" k="foot.h1" />
              <a href={LINKS.blog} target="_blank" rel="noreferrer"><T k="foot.blog" /></a>
              <a href={LINKS.youtube} target="_blank" rel="noreferrer"><T k="foot.youtube" /></a>
              <a href={LINKS.facebook} target="_blank" rel="noreferrer"><T k="foot.facebook" /></a>
            </div>
            <div />
          </div>
          <div className={cx("fbot")}>
            <span>© 2026 CROSSBIM Inc. All rights reserved.</span>
            <T as="span" k="foot.tag" />
          </div>
        </div>
      </footer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 아이콘 스프라이트 (한 번만 렌더링되는 숨겨진 SVG defs)
// 원본에서 누락돼 렌더링되지 않던 i-building, i-helmet2 를 추가했습니다.
// ─────────────────────────────────────────────────────────────
function IconSprite() {
  const s = { fill: "none", strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" };
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
      <defs>
        <g id="i-layers" {...s}><path d="M12 2 2 7l10 5 10-5-10-5Z" /><path d="m2 17 10 5 10-5" /><path d="m2 12 10 5 10-5" /></g>
        <g id="i-cube" {...s}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5M12 22V12" /></g>
        <g id="i-doc" {...s}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6M8 13h8M8 17h6" /></g>
        <g id="i-check" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m4 12 5 5L20 6" /></g>
        <g id="i-checkbox" {...s}><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></g>
        <g id="i-brief" {...s}><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></g>
        <g id="i-clip" {...s}><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M9 13h6M9 17h4" /></g>
        <g id="i-helmet" {...s}><path d="M3 16a9 9 0 0 1 18 0" /><path d="M2 16h20v2a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-2Z" /><path d="M9 7.5V4.6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2.9" /></g>
        <g id="i-mail" {...s}><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 6L2 7" /></g>
        <g id="i-l" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></g>
        <g id="i-r" fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></g>
        <g id="i-menu" fill="none" strokeWidth={1.8} strokeLinecap="round"><path d="M4 7h16M4 12h16M4 17h16" /></g>
        <g id="i-chart" {...s}><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></g>
        <g id="i-flask" {...s}><path d="M9 2h6M10 2v6l-6 11a2 2 0 0 0 2 3h12a2 2 0 0 0 2-3L14 8V2" /><path d="M8 15h8" /></g>
        <g id="i-crack" {...s}><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M8 4c2 4-2 6 1 9s-1 5 1 7" /></g>
        <g id="i-grid" {...s}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></g>
        <g id="i-calendar" {...s}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></g>
        <g id="i-folder" {...s}><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" /></g>
        {/* 원본에서 참조만 되고 정의가 빠져 있던 아이콘 추가 */}
        <g id="i-building" {...s}><path d="M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16" /><path d="M15 9h4a1 1 0 0 1 1 1v11" /><path d="M2 21h20" /><path d="M8 8h.01M11 8h.01M8 12h.01M11 12h.01M8 16h.01M11 16h.01" /></g>
        <g id="i-helmet2" {...s}><rect x="2" y="16.2" width="20" height="3.2" rx="1.6" /><path d="M3.5 16.2v-2.4C3.5 9.8 5.7 6.9 9.2 5.8" /><path d="M20.5 16.2v-2.4c0-4-2.2-6.9-5.7-8" /><path d="M9.2 11.7V6.6a1.8 1.8 0 0 1 1.8-1.8h2a1.8 1.8 0 0 1 1.8 1.8v5.1" /></g>      </defs>
    </svg>
  );
}
